import React, { PureComponent } from 'react';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import { FileInput } from 'react-admin';
import FileFieldCustom from '../../../components/FileFieldCustom/FileFieldCustom';
import VideoPickerButton, { SelectedVideoDisplay } from '../../../components/VideoPickerButton';
import uploadVideoService from '../../../utils/uploadVideo';
import countUploading from '../../../utils/countUploading';

class IntroScreensComponent extends PureComponent {
  constructor(props) {
    super(props);
    const { value } = props;
    const initialVideo = value.introScreens[1] ? value.introScreens[1].video : null;
    this.state = {
      introScreen1: {
        title: value.name,
        text: value.introScreens[0] ? value.introScreens[0].text : '',
      },
      introScreen2: {
        title: value.introScreens[1] ? value.introScreens[1].title : '',
        text: value.introScreens[1] ? value.introScreens[1].text : '',
        video: initialVideo,
      },
      selectedLibraryVideo: null,
      initialVideo,
      uploading: false,
      uploadProgress: 0,
      uploadError: null,
    };
  }

  componentDidMount() {
    this.props.handleData(this.converToServerFormat(this.state));
  }

  componentWillUnmount() {
    if (this.props.onUploadingChange) {
      this.props.onUploadingChange(false);
    }
  }

  getValue = (value, name) => {
    return value ? value[name] : '';
  };

  setUploading = (uploading) => {
    this.setState({ uploading });
    if (this.props.onUploadingChange) {
      this.props.onUploadingChange(uploading);
    }
  };

  handleChangeFirst = (event) => {
    const newState = {
      ...this.state,
      introScreen1: { ...this.state.introScreen1, text: event.target.value },
    };
    this.setState(newState);
    this.props.handleData(this.converToServerFormat(newState));
  };

  handleChangeSecond = (event) => {
    const newState = {
      ...this.state,
      introScreen2: {
        ...this.state.introScreen2,
        [event.target.name]: event.target.value,
      },
    };
    this.setState(newState);
    this.props.handleData(this.converToServerFormat(newState));
  };

  converToServerFormat = (data) => {
    return [{ ...data.introScreen1 }, { ...data.introScreen2 }];
  };

  handleFile = async (fileOrFiles) => {
    let file = null;

    // FileInput's onDrop callback gets an array of File objects.
    // Its onChange callback gets the transformed { rawFile, src, title } shape.
    if (Array.isArray(fileOrFiles) && fileOrFiles.length > 0) {
      file = fileOrFiles[0];
    } else if (fileOrFiles && fileOrFiles.rawFile) {
      file = fileOrFiles.rawFile;
    } else {
      return;
    }

    this.setUploading(true);
    this.setState({ uploadProgress: 0, uploadError: null });

    try {
      const result = await uploadVideoService({ rawFile: file }, (progressEvent) => {
        this.setState({ uploadProgress: countUploading(progressEvent) });
      });

      if (result && result.id) {
        const videoData = { id: result.id, originalName: file.name };
        const newState = {
          ...this.state,
          introScreen2: { ...this.state.introScreen2, video: videoData },
          selectedLibraryVideo: null,
          uploadProgress: 0,
          uploadError: null,
        };
        this.setState(newState);
        this.props.handleData(this.converToServerFormat(newState));
      } else {
        this.setState({ uploadError: 'Upload failed. Please try again.' });
      }
    } catch (e) {
      this.setState({ uploadError: e?.message || 'Upload failed. Please try again.' });
    } finally {
      this.setUploading(false);
    }
  };

  handleLibraryVideoSelect = (video) => {
    const newState = {
      ...this.state,
      introScreen2: { ...this.state.introScreen2, video },
      selectedLibraryVideo: video,
    };
    this.setState(newState);
    this.props.handleData(this.converToServerFormat(newState));
  };

  handleLibraryVideoClear = () => {
    const newState = {
      ...this.state,
      introScreen2: { ...this.state.introScreen2, video: this.state.initialVideo },
      selectedLibraryVideo: null,
    };
    this.setState(newState);
    this.props.handleData(this.converToServerFormat(newState));
  };

  render() {
    const { value } = this.props;
    const { selectedLibraryVideo, uploading, uploadProgress, uploadError } = this.state;
    return (
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <TextField
            placeholder={`Welcome to ${this.state.introScreen1.title}`}
            disabled
            fullWidth
            margin="normal"
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            name="introScreen1.text"
            placeholder="Text"
            fullWidth
            value={this.state.introScreen1.text}
            onChange={this.handleChangeFirst}
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <TextField
            placeholder="Title"
            name="title"
            fullWidth
            value={this.state.introScreen2.title}
            onChange={this.handleChangeSecond}
            margin="normal"
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <FileInput
            options={{ onDrop: this.handleFile }}
            defaultValue={this.state.introScreen2.video || undefined}
            format={(v) => (typeof v === 'string' ? { picture: v } : v)}
            source="video"
            label="Screen video (upload new)"
            accept={{ 'video/mp4': ['.mp4'] }}
          >
            <FileFieldCustom
              source="src"
              title="fileName"
              isSubtitle
              values={value}
              checkedResource="programs"
              checkedName="introScreens[1].video"
            />
          </FileInput>
          {uploading && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2">Uploading {uploadProgress}%</Typography>
              <LinearProgress value={uploadProgress} variant="determinate" />
            </Box>
          )}
          {uploadError && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {uploadError}
            </Typography>
          )}
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Or select an existing video from the library:
            </Typography>
            <SelectedVideoDisplay
              video={selectedLibraryVideo}
              onClear={this.handleLibraryVideoClear}
            />
            <VideoPickerButton onSelect={this.handleLibraryVideoSelect} />
          </Box>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            name="text"
            placeholder="Text"
            fullWidth
            value={this.state.introScreen2.text}
            onChange={this.handleChangeSecond}
          />
        </Grid>
      </Grid>
    );
  }
}
export default IntroScreensComponent;
